import * as Sentry from "@sentry/react";
import APIRequest from "system/APIRequest";
import { QueryBranch } from "./QueryBranch";
import { QueryLeafNode } from "./QueryLeafNode";
import { QuerySpan } from "./QuerySpan";
import { loadSegment, queryOpIsBranch } from "./QueryUtility";
import { merge } from "lodash";
import { t } from "i18next";
import { InfegyAccountStub, InfegyUserStub } from "system/InfegyUser";

export const exampleQuery:string = '{"dataset_id":"ds_gj4u3F40SLa","user":{},"query":{"op":"and","values":[{"op":"boolean","fields":["title","body","description"],"value":"banana (pie OR cake OR pies OR cakes) (recipe OR recipes) NOT (peanut OR peanuts)"},{"op":"=","field":"likes","value":100},{"op":">","field":"published","value":"P0DC"},{"op":"span","distance":2,"field":"body","left":{"op":"or","values":[{"op":"contains","value":"pie"},{"op":"contains","value":"pies"}]},"right":{"op":"contains","value":"bananas"}},{"op":"not","values":[{"op":"contains","field":"channels","value":"TwiTtEr"},{"op":"contains","field":"body","value":"testing. Does this work?"}]}]}}';
export const defaultQuery:any = { query: { op: "and", values: [] } };
export const socialDatasetId:string = "ds_gj4u3F40SLa";

// Available Query Ops
// Can be: "or, and, not, contains, =, <, <=, >, >=, span, boolean, range
export enum queryOps {
  or = "or",
  and = "and",
  not = "not",
  contains = "contains",
  equals = "=",
  lessThan = "<",
  lessThanOrEqual = "<=",
  greaterThan = ">",
  greaterThanOrEqual = ">=",
  span = "span",
  boolean = "boolean",
  range = "range"
}

export class Query {
  static async delete(queryId: string): Promise<boolean> {
    const response: any = await APIRequest(`obj/query/${queryId}`, {}, { method: "DELETE" });
    return response.success;
  }

  // Required fields
  name: string = "";
  dataset_id: string = "";
  query: QueryBranch | QueryLeafNode | QuerySpan = new QueryBranch({op: queryOps.and});

  // Optional: If the query was generated by AI, this is the prompt that was used
  prompt?: string;

  // Optional fields for starscape database rows, not actually needed for API queries
  id?: string;

  is_ephemeral?: boolean;
  is_saved?: boolean;

  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;

  access?: string;

  user_id?: string;
  account_id?: string;

  meta: any | null = null;

  _modifiable?: boolean;
  _owned_by_you?: boolean;

  _deletable?: boolean;
  _deleted?: boolean;
  _is_shared?: boolean;

  _user?: InfegyUserStub;

  _account?: InfegyAccountStub;

  constructor(query: any) {
    let queryToLoad = query;
    if (typeof queryToLoad === "string") {
      const queryString = queryToLoad.trim();
      if (queryString.startsWith("{")) {
        queryToLoad = JSON.parse(queryString);
      }
    }
    this.loadFromObject(queryToLoad);
    return this;
  }

  loadFromObject(queryToLoad: any) {
    if (typeof queryToLoad === "string") {
      try {
        queryToLoad = JSON.parse(queryToLoad);
      } catch (error) {
        console.error(`Error parsing query string: ${error}`);
        Sentry.captureException(error);
      }
    }
    if (typeof queryToLoad === "object") {
      try {
        Object.assign(this, queryToLoad);
      } catch (error) {
        console.error(`Error assigning query object: ${error}`);
        Sentry.captureException(error);
      }

      let queryBodyToLoad = queryToLoad.query;
      if (typeof(queryBodyToLoad) === "string") {
        queryBodyToLoad = JSON.parse(queryBodyToLoad);
      }
      this.query = loadSegment(queryBodyToLoad);

      if (queryToLoad.id) {
        this.id = queryToLoad.id || "";
      }
      this.dataset_id = queryToLoad.dataset_id || "";
      this.name = queryToLoad.name || "";
      this.prompt = queryToLoad.prompt || "";
    }
  }

  toAPIQuery() {
    const queryObj:any = {
      dataset_id: this.dataset_id,
      query: this.query.toAPIQuery()
    }
    if (this.name) { queryObj.name = this.name; }
    if (this.prompt) { queryObj.prompt = this.prompt; }
    return queryObj;
  }
}

export interface mergeQueriesOptions {
  // If true, the datasets will not be checked for equality - use with caution!
  ignoreDatasets?: boolean;
  // If provided, the merged query will use this op instead of "AND"
  op?: queryOps;
  // If provided, this will override the merged query name
  mergedNameOverride?: string;
  // If true, the prompt of the first query will be preserved in the merged query
  preserveFirstQueryPrompt?: boolean;
  // If true, the merged query will be flattened if the ops are the same as the target op
  flatten?: boolean;
  // Preserve the first query's ID
  preserveId?: boolean;
}

export function mergeQueries(queries: Array<Query>, options: mergeQueriesOptions = {}): Query {
  const mergableQueries = queries.filter(q => {
    return q && q.query && q.query.op;
  });

  if (mergableQueries.length < 2) {
    return mergableQueries[0] || new Query({});
  }

  if (!options.ignoreDatasets && mergableQueries.length > 1) {
    // check for unmatched datasets
    const datasetId = mergableQueries[0].dataset_id;
    const unmatchedDatasets = mergableQueries.filter((query) => query.dataset_id !== datasetId);
    if (unmatchedDatasets.length > 0) {
      console.error(`Cannot merge queries from different datasets: ${unmatchedDatasets.map(q => q.dataset_id).join(", ")}`);
      Sentry.captureMessage(`Cannot merge queries from different datasets: ${unmatchedDatasets.map(q => q.dataset_id).join(", ")}`);
      return mergableQueries[0];
    }
  }

  // Use the first query as the base for merging
  const mergedQuery = new Query(mergableQueries[0]);

  let activeRootBranch = mergedQuery.query as QueryBranch;

  // if flatten is true, the first query has a root AND branch, and the target op is "AND",
  // we can make a cleaner merge by using the existing root branch
  let skipFirstQuery = false;
  if (mergedQuery.query.op === queryOps.and && activeRootBranch && activeRootBranch.op === queryOps.and && options.flatten && options.op === queryOps.and) {
    skipFirstQuery = true;
  } else {
    // Otherwise, we create a new root branch
    mergedQuery.query = new QueryBranch({ op: options.op || queryOps.and, values: [] });
    activeRootBranch = mergedQuery.query as QueryBranch;
  }

  mergableQueries.forEach((query, index) => {
    if (index === 0 && skipFirstQuery) {
      // Skip the first query if we're using its root branch
      return;
    }
    if (!activeRootBranch.values || !query.query) {
      return;
    }
    // Skip empty queries
    if (queryOpIsBranch(query.query.op) && (!("values" in query.query) || !Array.isArray(query.query.values) || query.query.values.length === 0)) {
      return;
    }

    // We can provide a cleaner merge if flatten is true and the current query is an AND branch
    if (options.flatten && activeRootBranch.op === queryOps.and && query.query.op === queryOps.and) {
      // If the current query is an AND branch, we can merge its values directly
      if ("values" in query.query && Array.isArray(query.query.values)) {
        query.query.values.forEach((value) => {
          if (value && activeRootBranch.values && typeof value === "object" && !Array.isArray(value)) {
            activeRootBranch.values.push(value);
          }
        });
      }
      return;
    }

    if (activeRootBranch.values) {
      activeRootBranch.values.push(query.query);
    }
  });

  // Merge names
  if (options.mergedNameOverride) {
    mergedQuery.name = options.mergedNameOverride;
  } else {
    mergedQuery.name = mergableQueries.filter(q => q.name).map(q => q.name).join(" + ");
  }
  // Limit the name length to 255 characters
  mergedQuery.name = mergedQuery.name.substring(0, 255);

  const trimmedQuery = new Query({});
  trimmedQuery.name = mergedQuery.name;
  trimmedQuery.dataset_id = mergedQuery.dataset_id;
  trimmedQuery.query = mergedQuery.query;
  if (options.preserveFirstQueryPrompt) {
    trimmedQuery.prompt = mergableQueries[0].prompt || undefined;
  }
  if (options.preserveId) {
    trimmedQuery.id = mergableQueries[0].id;
  }

  return trimmedQuery;
}